/**
 * The harvest festival, the idle game's prestige (D244, D245, D246). Holding it ends the year:
 * producers, upgrades, goals, chains and coins reset, and the world is paid Heritage from the
 * square root of the year's earnings. Heritage earned raises all income for good; Heritage to
 * spend buys festival perks. The barn, the perks and lifetime figures are kept, and each
 * festival leaves a mark on the map.
 */
import type { IdleContent, IdlePerkT, IdleTraitT } from '@hedgerow/data';
import { idleEndTimed } from './bonuses.ts';
import { noteIdleCollection } from './collection.ts';
import { idleFillLineage, idleLineageMeter } from './lineage.ts';
import { idlePerkClosedBy, idlePerkTierShort } from './tree.ts';
import {
  checkInIdle,
  idleBeginYear,
  idleFestivalsThisGeneration,
  idleHandsHired,
  normaliseIdle,
  type IdleResult,
} from './world.ts';
import {
  idleClamp,
  idleCoinsForHeritage,
  idleFestivalThreshold,
  idleHeritageFor,
  idleHeritageForBonus,
  idleHeritageMultiplier,
} from './rules.ts';
import {
  newIdleYear,
  type IdleWorldState,
  type IdleLineageState,
  type IdleYearState,
} from './state.ts';

/** The level of a perk the world owns, 0 when it owns none. */
export function idlePerkLevel(state: IdleWorldState, id: string): number {
  return state.permanent.perks[id] ?? 0;
}

/** Heritage to spend on the next level of a perk, when `level` are owned. */
export function idlePerkCost(perk: IdlePerkT, level: number): number {
  // D336: past `lateGrowth.from` levels, each level costs `lateGrowth.growth` times the last.
  const late = perk.lateGrowth;
  if (late && level > late.from)
    return Math.round(
      perk.cost * Math.pow(perk.costGrowth, late.from) * Math.pow(late.growth, level - late.from),
    );
  return Math.round(perk.cost * Math.pow(perk.costGrowth, level));
}

/** The income multiplier from Heritage earned. It grows more slowly than Heritage (D249). */
export function idleHeritageBonus(content: IdleContent, heritageEarned: number): number {
  return idleHeritageMultiplier(content, heritageEarned);
}

/**
 * Heritage this generation's earnings have paid for: what its festivals paid from earnings, and
 * for a save made under the square-root rule, the gap the change of rule left (D299).
 */
function heritagePaid(state: IdleWorldState): number {
  const from = state.permanent.lineage?.festivalsBefore ?? 0;
  const paid = state.permanent.festivals
    .slice(from)
    .reduce((a, f) => a + f.heritage - (f.fromGoals ?? 0), 0);
  return paid + (state.permanent.heritageOffset ?? 0);
}

/**
 * D299: the Heritage this year's goals add to a festival paying `earned` from earnings: what
 * they earned, but never more than `festival.goalShare` of the festival's whole Heritage.
 */
export function idleFestivalFromGoals(
  state: IdleWorldState,
  content: IdleContent,
  earned: number,
): number {
  const goals = state.year.heritage ?? 0;
  const share = content.festival.goalShare;
  if (share === undefined) return goals;
  return Math.min(goals, Math.floor((earned * share) / (1 - share)));
}

/** Heritage this generation must pass before its festivals pay (D264). */
function generationFloor(state: IdleWorldState): number {
  return state.permanent.lineage?.heritageFloor ?? 0;
}

/**
 * D477: what the world's traits multiply the Heritage festivals pay from earnings by: for each
 * `heritageGain` trait, 1 plus its gain for every level, so each level adds the same and the
 * gain never compounds. 1 with none.
 */
export function idleHeritageGain(state: IdleWorldState, content: IdleContent): number {
  let gain = 1;
  for (const t of content.lineage.traits)
    if (t.heritageGain) gain *= 1 + t.heritageGain * idleTraitLevel(state, t.id);
  return gain;
}

/** D477: Heritage from earnings, with the traits' gain. */
function gainedHeritage(state: IdleWorldState, content: IdleContent, earned: number): number {
  const base = idleHeritageFor(content, earned);
  const gain = idleHeritageGain(state, content);
  return gain === 1 ? base : idleClamp(Math.floor(base * gain));
}

/** D477: the fewest coins that pay `heritage` Heritage, with the traits' gain. */
function coinsForGained(state: IdleWorldState, content: IdleContent, heritage: number): number {
  const gain = idleHeritageGain(state, content);
  return idleCoinsForHeritage(content, gain === 1 ? heritage : Math.ceil(heritage / gain));
}

/**
 * D477: the Heritage a festival's readiness and least share are measured against: this
 * generation's, so Heritage kept from the last generation (which still lifts income) is left out.
 */
function generationHeritage(state: IdleWorldState): number {
  const earned = state.permanent.heritageEarned;
  return Math.max(0, earned - (state.permanent.lineage?.kept ?? 0));
}

/** Coins earned in this generation (D257). */
export function idleGenerationEarned(state: IdleWorldState): number {
  return state.permanent.lifetimeEarned - (state.permanent.lineage?.lifetimeBefore ?? 0);
}

/**
 * Heritage this year's earnings pay (D245, D249), 0 when they pay none. D321: never more than
 * `festival.maxStep` times what the generation's last festival paid from earnings, or what makes
 * this one worth holding if that is more, so Heritage grows without jumps. What is held back is
 * not lost: it is still owed by the generation's earnings and paid at the next festival.
 */
function earningsHeritage(state: IdleWorldState, content: IdleContent): number {
  if (content.festival.heritageFrom !== 'generation')
    return gainedHeritage(state, content, state.year.earned);
  const total = gainedHeritage(state, content, idleGenerationEarned(state));
  const owed = Math.max(0, total - heritagePaid(state) - generationFloor(state));
  const from = state.permanent.lineage?.festivalsBefore ?? 0;
  const last = state.permanent.festivals.slice(from).at(-1);
  const step = content.festival.maxStep;
  if (!last || step === undefined) return owed;
  const cap = Math.max(
    step * (last.heritage - (last.fromGoals ?? 0)),
    idleFestivalReadyHeritage(state, content),
  );
  return Math.min(owed, cap);
}

/** D321: true once the year has run `festival.ready.maxYearMs`, from the world's last advance. */
export function idleFestivalLate(state: IdleWorldState, content: IdleContent): boolean {
  return state.advancedTo - state.year.startedAt >= content.festival.ready.maxYearMs;
}

/**
 * D322: the Heritage, goals' share included, a festival must pay to lift all income by
 * `festival.ready.lift`: the Heritage earned that gives that bonus, less the Heritage earned now.
 * D477: measured against this generation's Heritage, kept Heritage left out.
 */
export function idleFestivalReadyHeritage(state: IdleWorldState, content: IdleContent): number {
  const held = generationHeritage(state);
  const want = idleHeritageForBonus(
    content,
    idleHeritageBonus(content, held) * content.festival.ready.lift,
  );
  return Math.max(idleFestivalLeast(state, content), Math.ceil(want - held));
}

/**
 * Heritage the festival would pay now from earnings (D245, D249), 0 when it would pay none.
 * D321: once the year has run `festival.ready.maxYearMs`, it pays at least what makes the
 * festival worth holding, goals' Heritage counted, so a worthwhile festival is never further
 * off than that. What it pays beyond its earnings counts as paid, so later festivals catch up.
 */
export function idleFestivalHeritage(state: IdleWorldState, content: IdleContent): number {
  const base = earningsHeritage(state, content);
  if (!idleFestivalLate(state, content)) return base;
  const goals = idleFestivalFromGoals(state, content, base);
  return Math.max(base, idleFestivalReadyHeritage(state, content) - goals);
}

/** D322: the income multiplier holding the festival now would give: x1.4 is 40% more. */
export function idleFestivalLift(state: IdleWorldState, content: IdleContent): number {
  const earned = idleFestivalHeritage(state, content);
  const heritage = earned + idleFestivalFromGoals(state, content, earned);
  const held = state.permanent.heritageEarned;
  return idleHeritageBonus(content, held + heritage) / idleHeritageBonus(content, held);
}

/**
 * The least Heritage a festival may pay: one, or a share of the Heritage held, so a festival is
 * never near-free (D264, D384). D477: a share of this generation's Heritage, kept Heritage left
 * out, so a new generation's first festivals are not held back by what it kept.
 */
export function idleFestivalLeast(state: IdleWorldState, content: IdleContent): number {
  return Math.max(1, Math.ceil(generationHeritage(state) * (content.festival.minShare ?? 0)));
}

/**
 * Coins the year must still earn before the festival can be held; 0 when it can (D256). A late
 * year (D321) needs only `minYearEarned`, since the festival then pays whatever it earned.
 */
export function idleFestivalNeeds(state: IdleWorldState, content: IdleContent): number {
  if (content.festival.heritageFrom !== 'generation')
    return Math.max(0, idleFestivalThreshold(content) - state.year.earned);
  if (idleFestivalLate(state, content))
    return Math.max(0, content.festival.minYearEarned - state.year.earned);
  const forOne =
    coinsForGained(
      state,
      content,
      heritagePaid(state) + generationFloor(state) + idleFestivalLeast(state, content),
    ) - idleGenerationEarned(state);
  return Math.max(0, content.festival.minYearEarned - state.year.earned, forOne);
}

/**
 * D299: coins the year must still earn for the festival to pay `heritage` from earnings, goals
 * aside; 0 when it would already.
 */
export function idleFestivalCoinsFor(
  state: IdleWorldState,
  content: IdleContent,
  heritage: number,
): number {
  if (content.festival.heritageFrom !== 'generation')
    return Math.max(0, coinsForGained(state, content, heritage) - state.year.earned);
  const want = heritagePaid(state) + generationFloor(state) + heritage;
  return Math.max(0, coinsForGained(state, content, want) - idleGenerationEarned(state));
}

/** True when the festival can be held: exactly when it would pay at least one Heritage (D256). */
export function idleFestivalAvailable(state: IdleWorldState, content: IdleContent): boolean {
  return (
    !state.festivalPending &&
    idleFestivalNeeds(state, content) === 0 &&
    idleFestivalHeritage(state, content) >= idleFestivalLeast(state, content)
  );
}

/**
 * D383: how long a year must run before its festival can be ready: `ready.minYearMs`, grown
 * `ready.floorGrowth` times for each festival held in this generation, never past
 * `ready.maxYearMs`. Counted per generation, so a new generation starts quick again.
 */
export function idleFestivalFloorMs(state: IdleWorldState, content: IdleContent): number {
  const r = content.festival.ready;
  const grown = r.minYearMs * Math.pow(r.floorGrowth ?? 1, idleFestivalsThisGeneration(state));
  return Math.min(r.maxYearMs, Math.round(grown));
}

/** D385: the share of the floor the year has run, 1 once it has. */
function floorShare(state: IdleWorldState, content: IdleContent, readyInMs: number): number {
  const floor = idleFestivalFloorMs(state, content);
  return floor > 0 ? 1 - readyInMs / floor : 1;
}

/** D383, D385: time the year must still run before its festival can be ready; 0 once it has. */
export function idleFestivalReadyInMs(state: IdleWorldState, content: IdleContent): number {
  return Math.max(
    0,
    idleFestivalFloorMs(state, content) - (state.advancedTo - state.year.startedAt),
  );
}

/** D355: festivals still to hold in this generation before the perk can be bought; 0 when it can. */
export function idlePerkFestivalsNeeded(state: IdleWorldState, perk: IdlePerkT): number {
  return Math.max(0, (perk.needsFestivals ?? 0) - idleFestivalsThisGeneration(state));
}

/** D367: the perk that must be owned before this one can be bought, or undefined when none. */
export function idlePerkNeedsPerk(state: IdleWorldState, perk: IdlePerkT): string | undefined {
  if (perk.needsPerk === undefined || idlePerkLevel(state, perk.needsPerk) > 0) return undefined;
  return perk.needsPerk;
}

/**
 * D355, D367, D454: true when nothing but Heritage stands between the world and the perk's next
 * level: not at its top, its festivals held, the perk it needs owned, its tier open (Heritage
 * spent in its branch) and its fork's other side not owned.
 */
export function idlePerkOpen(
  state: IdleWorldState,
  content: IdleContent,
  perk: IdlePerkT,
): boolean {
  return (
    !idlePerkAtTop(state, perk) &&
    idlePerkFestivalsNeeded(state, perk) === 0 &&
    idlePerkNeedsPerk(state, perk) === undefined &&
    idlePerkTierShort(state, content, perk) === 0 &&
    idlePerkClosedBy(state, content, perk) === undefined
  );
}

/** True when the perk has a top level and the world owns it. */
export function idlePerkAtTop(state: IdleWorldState, perk: IdlePerkT): boolean {
  return perk.maxLevel !== undefined && idlePerkLevel(state, perk.id) >= perk.maxLevel;
}

export interface IdleFestivalPreview {
  available: boolean;
  /** Heritage the festival would pay now, goals' Heritage included. */
  heritage: number;
  /** The part of `heritage` this year's goals earned (D252). */
  fromGoals: number;
  /** Coins the year must still earn before the festival can be held; 0 when it can. */
  needs: number;
  /** The income multiplier from Heritage now, and after this festival. */
  bonusNow: number;
  bonusAfter: number;
  /** D322: what holding now multiplies income by, `bonusAfter / bonusNow`. */
  lift: number;
  /** D322: true when that lift is at least `festival.ready.lift`: worth holding. */
  ready: boolean;
  /** D322: how far the year has come towards ready, 0 to 1, by the Heritage it would pay. */
  progress: number;
  /**
   * D383, D385: time the year must still run before it can be ready, 0 once it has. When the
   * Heritage is met (`heritage >= readyHeritage`) but this is above 0, the festival is waiting
   * on time alone: "Ready in 25m".
   */
  readyInMs: number;
  /** D383, D385: the whole time this year must run before it can be ready, for a bar by time. */
  floorMs: number;
  /** D322: the Heritage, goals' share included, that makes the festival ready. */
  readyHeritage: number;
  /** The mark this festival would leave on the map. */
  mark: string;
}

/** What holding the festival now would pay, for the festival screen. */
export function idleFestivalPreview(
  state: IdleWorldState,
  content: IdleContent,
): IdleFestivalPreview {
  const earnedNow = idleFestivalHeritage(state, content);
  const fromGoals = idleFestivalFromGoals(state, content, earnedNow);
  const heritage = earnedNow + fromGoals;
  const earned = state.permanent.heritageEarned;
  const marks = content.festival.marks;
  const available = idleFestivalAvailable(state, content);
  const bonusNow = idleHeritageBonus(content, earned);
  const bonusAfter = idleHeritageBonus(content, earned + heritage);
  const want = idleFestivalReadyHeritage(state, content);
  const readyInMs = idleFestivalReadyInMs(state, content);
  const ready = available && heritage >= want && readyInMs === 0;
  return {
    available,
    heritage,
    fromGoals,
    needs: idleFestivalNeeds(state, content),
    bonusNow,
    bonusAfter,
    lift: bonusAfter / bonusNow,
    ready,
    // D385: short of ready, the lesser of the Heritage share and the time share, so a bar is
    // never full while the year must still run.
    progress: ready
      ? 1
      : Math.max(0, Math.min(0.999, heritage / want, floorShare(state, content, readyInMs))),
    readyInMs,
    floorMs: idleFestivalFloorMs(state, content),
    readyHeritage: want,
    mark: marks[state.permanent.festivals.length % marks.length] ?? '',
  };
}

/** D295: coins a perk adds to the start of every year with `level` levels owned. */
export function idlePerkStartCoins(perk: IdlePerkT, level: number): number {
  return (perk.startCoins ?? 0) * level;
}

/** D295: how many of its producers a trait starts every year with, with `level` levels owned. */
export function idleTraitStartCount(trait: IdleTraitT, level: number): number {
  const list = trait.startProducers ?? [];
  if (level <= 0) return 0;
  return Math.min(list.length, (trait.startFirst ?? list.length) + level - 1);
}

/** A new year with the head start the world's perks give: coins, producers and hands. */
export function idleStartingYear(
  state: IdleWorldState,
  content: IdleContent,
  number: number,
  now: number,
): IdleYearState {
  const year = newIdleYear(content, number, now);
  // D386: unlock and hand prices follow the Heritage the year starts with, fixed all year.
  const power = content.prices.heritagePower ?? 0;
  if (power > 0) {
    const factor = Math.pow(idleHeritageBonus(content, state.permanent.heritageEarned), power);
    if (factor > 1) year.priceFactor = factor;
  }
  const handsTrait = idleTraitHands(state, content);
  for (const t of content.lineage.traits) {
    const level = idleTraitLevel(state, t.id);
    if (level === 0) continue;
    const list = t.startProducers ?? [];
    for (const id of list.slice(0, idleTraitStartCount(t, level))) {
      const p = content.producers.find((x) => x.id === id);
      if (p && !year.producers[id])
        year.producers[id] = {
          owned: 1,
          hand: true,
          running: true,
          progressMs: 0,
          cycleMs: p.cycleMs,
        };
    }
  }
  for (const perk of content.perks) {
    const level = idlePerkLevel(state, perk.id);
    if (level === 0) continue;
    year.coins += idlePerkStartCoins(perk, level);
    for (const id of perk.startProducers ?? []) {
      const p = content.producers.find((x) => x.id === id);
      if (p && !year.producers[id])
        year.producers[id] = {
          owned: 1,
          hand: false,
          running: false,
          progressMs: 0,
          cycleMs: p.cycleMs,
        };
    }
    for (const id of perk.startHands ?? []) {
      const ps = year.producers[id];
      if (ps) year.producers[id] = { ...ps, hand: true, running: true };
    }
  }
  // With the hands trait (D257), whatever the year starts with is run by a hand.
  if (handsTrait)
    for (const [id, ps] of Object.entries(year.producers))
      if (!ps.hand) year.producers[id] = { ...ps, hand: true, running: true };
  return year;
}

/**
 * Hold the harvest festival: end the year, pay Heritage and leave a mark on the map. The new
 * year does not start yet (D276): the world waits, earning nothing, with Heritage to spend on
 * perks, until `startIdleYear`. The year that ended stays on the state until then, for the screen.
 */
export function holdIdleFestival(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  if (!idleFestivalAvailable(s, content)) {
    return {
      ok: false,
      reason: 'festival_not_ready',
      needs: Math.max(1, idleFestivalNeeds(s, content)),
    };
  }
  const { heritage, mark, fromGoals } = idleFestivalPreview(s, content);
  const p = s.permanent;
  const permanent = {
    ...p,
    heritage: idleClamp(p.heritage + heritage),
    heritageEarned: idleClamp(p.heritageEarned + heritage),
    festivals: [
      ...p.festivals,
      fromGoals > 0
        ? { year: s.year.number, heritage, mark, at: now, fromGoals }
        : { year: s.year.number, heritage, mark, at: now },
    ],
  };
  const pending = { year: s.year.number, heritage, at: now };
  // D370: the year has ended, so its timed bonuses end and its open offers are withdrawn.
  const ended = idleEndTimed(s, content, now);
  // D325: a festival held adds a little to the Lineage meter.
  const held = idleFillLineage(
    { ...ended, permanent, festivalPending: pending },
    content,
    'festival',
  );
  // D290: the mark goes in the collection the moment it is made.
  return { ok: true, state: noteIdleCollection(held, content, now) };
}

/**
 * Start the year after a held festival (D276), with the head start the perks bought give. A
 * player away longer than `festival.startAfterAwayMs` finds it started on their return.
 */
export function startIdleYear(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  // Started on this check-in already, because the player had been away.
  if (state.festivalPending && !s.festivalPending) return { ok: true, state: s };
  if (!s.festivalPending) return { ok: false, reason: 'not_pending' };
  return { ok: true, state: idleBeginYear(s, content, now) };
}

/** Buy the next level of a festival perk with Heritage to spend. Heritage earned is untouched (D246). */
export function buyIdlePerk(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const perk = content.perks.find((k) => k.id === id);
  if (!perk) return { ok: false, reason: 'unknown_perk' };
  const level = idlePerkLevel(s, id);
  if (perk.maxLevel !== undefined && level >= perk.maxLevel)
    return { ok: false, reason: 'perk_top_level' };
  const festivals = idlePerkFestivalsNeeded(s, perk);
  if (festivals > 0) return { ok: false, reason: 'needs_festivals', needs: festivals };
  if (idlePerkNeedsPerk(s, perk) !== undefined) return { ok: false, reason: 'needs_perk' };
  // D454: a tier opens by Heritage spent in its branch; a fork's other side closes it.
  const short = idlePerkTierShort(s, content, perk);
  if (short > 0) return { ok: false, reason: 'tier_locked', needs: short };
  if (idlePerkClosedBy(s, content, perk) !== undefined) return { ok: false, reason: 'fork_closed' };
  const cost = idlePerkCost(perk, level);
  if (s.permanent.heritage < cost)
    return { ok: false, reason: 'not_enough_heritage', needs: cost - s.permanent.heritage };
  const permanent = {
    ...s.permanent,
    heritage: s.permanent.heritage - cost,
    perks: { ...s.permanent.perks, [id]: level + 1 },
  };
  return { ok: true, state: normaliseIdle({ ...s, permanent }, content, now) };
}

/** The level of a Lineage trait the world owns, 0 when it owns none (D257). */
export function idleTraitLevel(state: IdleWorldState, id: string): number {
  return state.permanent.lineage?.traits[id] ?? 0;
}

/** Lineage to spend on the next level of a trait, when `level` are owned. */
export function idleTraitCost(trait: IdleTraitT, level: number): number {
  return Math.round(trait.cost * Math.pow(trait.costGrowth, level));
}

/** True when a trait the world owns makes every producer come with its hand hired. */
export function idleTraitHands(state: IdleWorldState, content: IdleContent): boolean {
  return idleHandsHired(state, content);
}

/** The generation the world is in: 1 until it is first passed on. */
export function idleGeneration(state: IdleWorldState): number {
  return state.permanent.lineage?.generation ?? 1;
}

/** Total Lineage a generation that earned this much Heritage is worth (D257, D263). */
export function idleLineageFor(content: IdleContent, heritageEarned: number): number {
  return Math.floor(content.lineage.perRootHeritage * Math.sqrt(Math.max(0, heritageEarned)));
}

export interface IdlePassOnPreview {
  available: boolean;
  /** Lineage passing on would pay now: `fromHeritage` and `fromPlay` together. */
  lineage: number;
  /** D325: the part the generation's Heritage pays. */
  fromHeritage: number;
  /** D325: the part the Lineage meter pays: its whole Lineage. */
  fromPlay: number;
  /** D325: the meter as it stands, whole and part. */
  meter: number;
  /** Festivals this generation must still hold before it can pass on; 0 when it can. */
  festivalsNeeded: number;
  generation: number;
}

/** What passing the world on would pay now, for the screen (D257). */
export function idlePassOnPreview(state: IdleWorldState, content: IdleContent): IdlePassOnPreview {
  // Lineage is paid on the best Heritage a generation has reached, less what it has paid
  // already, so passing on again without outgrowing the last generation pays nothing.
  const earned = state.permanent.lineage?.earned ?? 0;
  const fromHeritage = Math.max(
    0,
    idleLineageFor(content, state.permanent.heritageEarned) - earned,
  );
  const meter = idleLineageMeter(state);
  const fromPlay = Math.floor(meter);
  const lineage = fromHeritage + fromPlay;
  const festivalsNeeded = Math.max(
    0,
    content.lineage.minFestivals - idleFestivalsThisGeneration(state),
  );
  return {
    available: festivalsNeeded === 0 && lineage >= 1,
    lineage,
    fromHeritage,
    fromPlay,
    meter,
    festivalsNeeded,
    generation: idleGeneration(state),
  };
}

/** True when the world can be passed on to the next generation. */
export function idlePassOnAvailable(state: IdleWorldState, content: IdleContent): boolean {
  return idlePassOnPreview(state, content).available;
}

/** The barn level a new generation starts at, from the world's traits; 1 for none. */
export function idleTraitsBarnLevel(state: IdleWorldState, content: IdleContent): number {
  let level = 1;
  for (const t of content.lineage.traits)
    level = Math.max(level, idleTraitBarnLevel(content, t, idleTraitLevel(state, t.id)));
  return level;
}

/** D295: the barn level a trait starts each generation at with `level` levels owned; 1 for none. */
export function idleTraitBarnLevel(content: IdleContent, trait: IdleTraitT, level: number): number {
  if (!trait.startBarnLevel || level <= 0) return 1;
  return Math.min(trait.startBarnLevel + level - 1, content.barn.length);
}

/**
 * Pass the world on (D257): Heritage, perks, the barn and the year reset; every festival mark
 * stays on the land; the world is paid Lineage, which never resets. D388: like a festival, the
 * new generation waits (`festivalPending`, `passedOn`) while traits are chosen, and
 * `startIdleYear` begins it with their head start; a player away long enough finds it begun.
 */
export function passOnIdleWorld(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const preview = idlePassOnPreview(s, content);
  if (!preview.available) return { ok: false, reason: 'pass_on_not_ready' };
  const before: IdleLineageState = s.permanent.lineage ?? {
    points: 0,
    earned: 0,
    traits: {},
    generation: 1,
    festivalsBefore: 0,
  };
  const lineage: IdleLineageState = {
    ...before,
    points: before.points + preview.lineage,
    // D325: only Heritage's part counts here, so the meter never cuts what Heritage pays later.
    earned: before.earned + preview.fromHeritage,
    generation: before.generation + 1,
    festivalsBefore: s.permanent.festivals.length,
    lifetimeBefore: s.permanent.lifetimeEarned,
    lastHeritage: s.permanent.heritageEarned,
    heritageFloor: Math.floor(s.permanent.heritageEarned * content.lineage.heritageFloor),
  };
  lineage.kept = Math.floor(s.permanent.heritageEarned * keptShare(s, content));
  const kept = lineage.kept ?? 0;
  // D370: nothing timed carries into the new generation, and no open offer either.
  const ended = idleEndTimed(s, content, now);
  const passed: IdleWorldState = {
    ...ended,
    festivalPending: { year: s.year.number, heritage: kept, at: now, passedOn: true },
    permanent: {
      ...s.permanent,
      heritage: kept,
      heritageEarned: kept,
      heritageOffset: 0,
      // D325: the meter's whole Lineage is paid; the part left over carries on.
      lineageMeter: preview.meter - preview.fromPlay,
      perks: {},
      barnLevel: 1,
      lineage,
    },
  };
  return { ok: true, state: normaliseIdle(passed, content, now) };
}

/** A fresh year with the barn and head start the world's traits give. */
function dealYear(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
  number: number,
): IdleWorldState {
  const barnLevel = Math.max(state.permanent.barnLevel, idleTraitsBarnLevel(state, content));
  const kept = { ...state, permanent: { ...state.permanent, barnLevel } };
  const year = idleStartingYear(kept, content, number, now);
  return normaliseIdle({ ...kept, year }, content, now);
}

/** Buy the next level of a Lineage trait with Lineage (D257). */
export function buyIdleTrait(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const trait = content.lineage.traits.find((t) => t.id === id);
  if (!trait) return { ok: false, reason: 'unknown_trait' };
  const level = idleTraitLevel(s, id);
  if (trait.maxLevel !== undefined && level >= trait.maxLevel)
    return { ok: false, reason: 'trait_top_level' };
  const cost = idleTraitCost(trait, level);
  const lineage = s.permanent.lineage;
  const have = lineage?.points ?? 0;
  if (!lineage || have < cost)
    return { ok: false, reason: 'not_enough_lineage', needs: cost - have };
  // Keeping Heritage takes hold at once on the last generation's Heritage (D263).
  const last = lineage.lastHeritage ?? 0;
  const keep = trait.keepHeritage
    ? Math.floor(last * idleKeepShare(trait.keepHeritage, level + 1)) -
      Math.floor(last * idleKeepShare(trait.keepHeritage, level))
    : 0;
  const bought: IdleWorldState = {
    ...s,
    permanent: {
      ...s.permanent,
      heritage: s.permanent.heritage + keep,
      heritageEarned: s.permanent.heritageEarned + keep,
      lineage: {
        ...lineage,
        points: lineage.points - cost,
        kept: (lineage.kept ?? 0) + keep,
        traits: { ...lineage.traits, [id]: level + 1 },
      },
    },
  };
  // D388: while the new generation waits, its Heritage kept shows on the pending card too.
  if (bought.festivalPending?.passedOn)
    bought.festivalPending = {
      ...bought.festivalPending,
      heritage: bought.festivalPending.heritage + keep,
    };
  // Before anything is earned or bought in the year, the year is dealt again with the new head
  // start; a waiting generation (D388) is dealt by `startIdleYear` instead.
  const fresh = !s.festivalPending && s.year.earned === 0 && s.year.upgrades.length === 0;
  if (!fresh) return { ok: true, state: normaliseIdle(bought, content, now) };
  return { ok: true, state: dealYear(bought, content, now, s.year.number) };
}

/** The share of Heritage a passing on keeps, from the traits owned (D263). */
function keptShare(state: IdleWorldState, content: IdleContent): number {
  let kept = 1;
  for (const t of content.lineage.traits)
    if (t.keepHeritage) kept *= 1 - idleKeepShare(t.keepHeritage, idleTraitLevel(state, t.id));
  return 1 - kept;
}

/** The share kept with `level` levels of a trait keeping `share` of what is left each level. */
export function idleKeepShare(share: number, level: number): number {
  return 1 - Math.pow(1 - share, level);
}

/** True when the trait has a top level and the world owns it (D264). */
export function idleTraitAtTop(state: IdleWorldState, trait: IdleTraitT): boolean {
  return trait.maxLevel !== undefined && idleTraitLevel(state, trait.id) >= trait.maxLevel;
}

export interface IdleFestivalAdvice {
  /**
   * What the screen should say about the festival (D264, D322):
   * not_ready: it cannot be held yet; grow: it can, but it would lift income by less than
   * `festival.ready.lift`; hold: it is ready, worth holding now.
   */
  advice: 'not_ready' | 'grow' | 'hold';
  /** Heritage it would pay now, goals' share included. */
  heritage: number;
  /** D322: what holding now multiplies income by. */
  lift: number;
  /** D322: how far the year has come towards ready, 0 to 1. */
  progress: number;
  /** D322: Heritage it must pay to be ready. */
  readyHeritage: number;
  /** How long the year has run. */
  yearMs: number;
}

/**
 * Whether holding the festival now is the right move (D264, D322): once it lifts all income by at
 * least `festival.ready.lift`, or the year has run `festival.ready.maxYearMs` (D321).
 */
export function idleFestivalAdvice(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleFestivalAdvice {
  const p = idleFestivalPreview(state, content);
  const yearMs = Math.max(0, now - state.year.startedAt);
  const advice = !p.available ? 'not_ready' : p.ready ? 'hold' : 'grow';
  return {
    advice,
    heritage: p.heritage,
    lift: p.lift,
    progress: p.progress,
    readyHeritage: idleFestivalReadyHeritage(state, content),
    yearMs,
  };
}
